import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useFindMany } from "@gadgetinc/react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import RequireSignedInRole from "../components/RequireSignedInRole";
import { api } from "../api";

const REPLAY_RESUME_STORAGE_KEY = "voiceReplayLabResumeStateJson";

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatActionError(error) {
  const fallback = "Replay request failed";
  if (!error) return fallback;
  if (typeof error.message === "string" && error.message.trim()) return error.message;
  const firstGraphQLError = error.graphQLErrors?.[0];
  if (firstGraphQLError?.message) return firstGraphQLError.message;
  if (error.networkError?.message) return error.networkError.message;
  try {
    return JSON.stringify(error);
  } catch (_error) {
    return fallback;
  }
}

function ReplayLabContent() {
  const [scenarioIds, setScenarioIds] = useState([]);
  const [runsPerScenario, setRunsPerScenario] = useState(3);
  const [maxTurns, setMaxTurns] = useState(18);
  const [maxScenarios, setMaxScenarios] = useState(5);
  const [competencyLevel, setCompetencyLevel] = useState("intermediate");
  const [errorRate, setErrorRate] = useState(0.25);
  const [replayData, setReplayData] = useState(null);
  const [replayError, setReplayError] = useState(null);
  const [replayFetching, setReplayFetching] = useState(false);
  const [lastReplayMessage, setLastReplayMessage] = useState("");

  const [{ data: scenarios, fetching: scenariosFetching }] = useFindMany(api.clinicalScenario, {
    first: 200,
    filter: { isActive: { equals: true } },
    select: {
      id: true,
      title: true,
      levelOfCare: true,
      isActive: true,
    },
  });

  const scenarioOptions = useMemo(() => {
    return (scenarios || []).map((scenario) => ({
      value: String(scenario.id),
      label: `${scenario.title || "Untitled"} (${scenario.levelOfCare || "Unknown"})`,
    }));
  }, [scenarios]);

  const onRunReplayLab = async () => {
    setReplayFetching(true);
    setReplayError(null);
    const storedResumeStateJson = window.localStorage.getItem(REPLAY_RESUME_STORAGE_KEY);
    let resumeStateJson = storedResumeStateJson || null;
    let lastResult = null;
    try {
      for (let i = 0; i < 400; i++) {
        let result = null;
        let lastAttemptError = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            // eslint-disable-next-line no-await-in-loop
            result = await api.runVoiceSimulationReplayLab({
              scenarioIds: scenarioIds.length > 0 ? scenarioIds : undefined,
              runsPerScenario: Number(runsPerScenario),
              maxTurns: Number(maxTurns),
              maxScenarios: Number(maxScenarios),
              competencyLevel,
              errorRate: Number(errorRate),
              resumeStateJson,
            });
            break;
          } catch (error) {
            lastAttemptError = error;
            // eslint-disable-next-line no-await-in-loop
            await delay(300 * (attempt + 1));
          }
        }
        if (!result) {
          throw lastAttemptError || new Error("Replay request failed after retries");
        }
        if (!result || typeof result !== "object") {
          throw new Error("Replay action returned an invalid response payload");
        }
        lastResult = result;
        setReplayData(result);
        setLastReplayMessage(result?.message || "");
        if (!result?.inProgress) break;
        resumeStateJson = result?.resumeStateJson || null;
        if (!resumeStateJson) {
          throw new Error("Replay action reported inProgress without resumeStateJson");
        }
        window.localStorage.setItem(REPLAY_RESUME_STORAGE_KEY, resumeStateJson);
      }
      if (lastResult?.inProgress) {
        throw new Error("Replay loop reached client max iterations (400) before completion");
      }
      setReplayData(lastResult);
      setLastReplayMessage(lastResult?.message || "Replay lab completed");
      window.localStorage.removeItem(REPLAY_RESUME_STORAGE_KEY);
      return lastResult;
    } catch (error) {
      setReplayError(new Error(formatActionError(error)));
      return null;
    } finally {
      setReplayFetching(false);
    }
  };

  const summary = replayData?.summary;
  const runs = Array.isArray(replayData?.runs) ? replayData.runs : [];

  return (
    <Box minH="100vh" bg="#0f172a" color="#f8fafc" px={6} py={6}>
      <VStack align="stretch" spacing={6} maxW="1400px" mx="auto">
        <HStack justify="space-between" align="center">
          <Box>
            <Link to="/admin" style={{ color: "#93c5fd" }}>{"<- Back to Admin"}</Link>
            <Heading size="lg" mt={2}>Voice Replay Lab</Heading>
            <Text color="gray.400" mt={1}>
              Monte Carlo synthetic learner runs for simulator reliability testing.
            </Text>
          </Box>
          <Button as={Link} to="/admin/sim-sessions" colorScheme="pink" variant="outline">
            Open Voice Sim Review
          </Button>
        </HStack>

        <Box border="1px solid" borderColor="whiteAlpha.300" borderRadius="md" p={4} bg="whiteAlpha.100">
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            <FormControl>
              <FormLabel>Runs Per Scenario</FormLabel>
              <Input
                type="number"
                min={1}
                max={20}
                value={runsPerScenario}
                onChange={(e) => setRunsPerScenario(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Max Turns</FormLabel>
              <Input
                type="number"
                min={4}
                max={60}
                value={maxTurns}
                onChange={(e) => setMaxTurns(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Max Scenarios</FormLabel>
              <Input
                type="number"
                min={1}
                max={25}
                value={maxScenarios}
                onChange={(e) => setMaxScenarios(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Error Rate (0-0.9)</FormLabel>
              <Input
                type="number"
                min={0}
                max={0.9}
                step={0.05}
                value={errorRate}
                onChange={(e) => setErrorRate(e.target.value)}
              />
            </FormControl>
          </SimpleGrid>

          <HStack mt={4} spacing={3} align="center" wrap="wrap">
            <Button
              variant={competencyLevel === "novice" ? "solid" : "outline"}
              colorScheme={competencyLevel === "novice" ? "blue" : "whiteAlpha"}
              onClick={() => setCompetencyLevel("novice")}
              aria-pressed={competencyLevel === "novice"}
            >
              Novice
            </Button>
            <Button
              variant={competencyLevel === "intermediate" ? "solid" : "outline"}
              colorScheme={competencyLevel === "intermediate" ? "blue" : "whiteAlpha"}
              onClick={() => setCompetencyLevel("intermediate")}
              aria-pressed={competencyLevel === "intermediate"}
            >
              Intermediate
            </Button>
            <Button
              variant={competencyLevel === "strong" ? "solid" : "outline"}
              colorScheme={competencyLevel === "strong" ? "blue" : "whiteAlpha"}
              onClick={() => setCompetencyLevel("strong")}
              aria-pressed={competencyLevel === "strong"}
            >
              Strong
            </Button>
            <Badge colorScheme="purple">Selected: {competencyLevel}</Badge>
          </HStack>

          <FormControl mt={4}>
            <FormLabel>Scenario Scope (leave unchecked to use first active scenarios)</FormLabel>
            {scenariosFetching ? (
              <Spinner size="sm" />
            ) : (
              <CheckboxGroup value={scenarioIds} onChange={setScenarioIds}>
                <Stack maxH="240px" overflowY="auto" border="1px solid" borderColor="whiteAlpha.300" borderRadius="md" p={3}>
                  {scenarioOptions.map((scenario) => (
                    <Checkbox key={scenario.value} value={scenario.value}>
                      {scenario.label}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            )}
          </FormControl>

          <Button
            mt={4}
            colorScheme="purple"
            onClick={onRunReplayLab}
            isLoading={replayFetching}
            loadingText="Running replay lab..."
          >
            Run Replay Lab
          </Button>
        </Box>

        {replayError && (
          <Alert status="error">
            <AlertIcon />
            {replayError.message}
          </Alert>
        )}

        {lastReplayMessage && (
          <Alert status="info">
            <AlertIcon />
            {lastReplayMessage}
          </Alert>
        )}

        {summary && (
          <Box border="1px solid" borderColor="whiteAlpha.300" borderRadius="md" p={4} bg="whiteAlpha.100">
            <Heading size="md" mb={3}>Summary</Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat><StatLabel>Total Runs</StatLabel><StatNumber>{summary.totalRuns}</StatNumber></Stat>
              <Stat><StatLabel>Completion Rate</StatLabel><StatNumber>{summary.completionRate}%</StatNumber></Stat>
              <Stat><StatLabel>Avg Turns</StatLabel><StatNumber>{summary.avgTurns}</StatNumber></Stat>
              <Stat><StatLabel>Avg Interventions</StatLabel><StatNumber>{summary.avgInterventions}</StatNumber></Stat>
            </SimpleGrid>
            <HStack mt={3} spacing={3}>
              <Badge colorScheme="purple">Model: {summary.learnerModel}</Badge>
              <Badge colorScheme="blue">Competency: {summary.competencyLevel}</Badge>
              <Badge colorScheme="orange">Error Rate: {summary.errorRate}</Badge>
              <Badge colorScheme="teal">Scenarios: {summary.scenarioCount}</Badge>
            </HStack>
          </Box>
        )}

        {runs.length > 0 && (
          <Box border="1px solid" borderColor="whiteAlpha.300" borderRadius="md" p={4} bg="whiteAlpha.100">
            <Heading size="md" mb={3}>Run Results</Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="gray.200">Scenario</Th>
                    <Th color="gray.200">Final Phase</Th>
                    <Th color="gray.200">Turns</Th>
                    <Th color="gray.200">Transitions</Th>
                    <Th color="gray.200">Interventions</Th>
                    <Th color="gray.200">Mistakes</Th>
                    <Th color="gray.200">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {runs.map((run) => (
                    <Tr key={run.runId}>
                      <Td>{run.scenarioTitle}</Td>
                      <Td>{run.finalPhase}</Td>
                      <Td>{run.turns}</Td>
                      <Td>{run.transitionCount}</Td>
                      <Td>{run.interventionCount}</Td>
                      <Td>{run.mistakesInjected}</Td>
                      <Td>
                        <Badge colorScheme={run.completed ? "green" : "red"}>
                          {run.completed ? "Completed" : "Incomplete"}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

export default function AdminVoiceReplayLab() {
  return (
    <RequireSignedInRole role={["admin", "system-admin"]}>
      <ReplayLabContent />
    </RequireSignedInRole>
  );
}
